import { useId, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

const CARD =
  'flex w-full items-start gap-3 rounded-lg border bg-card text-left transition-colors';
const PAD = { default: 'p-3', sm: 'px-3 py-2' } as const;

function CardText({ title, description, badge }: { title: ReactNode; description?: ReactNode; badge?: ReactNode }) {
  return (
    <span className="min-w-0 flex-1 space-y-0.5">
      <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
        <span className="min-w-0 break-words">{title}</span>
        {badge}
      </span>
      {description ? <span className="block text-xs text-muted-foreground">{description}</span> : null}
    </span>
  );
}

type ChoiceCardBase = {
  title: ReactNode;
  description?: ReactNode;
  /** Before the text: a StoreAvatar, an icon. */
  media?: ReactNode;
  /** After the title: a Badge ("Coming soon"). */
  badge?: ReactNode;
  disabled?: boolean;
  /**
   * Why it's disabled ("Another set-up is being applied"), shown on hover and keyboard focus while
   * it's disabled and not loading, and read out with it. As Button's `disabledReason`.
   */
  disabledReason?: string | null;
  /** While its action runs: a spinner in place of the media, `loadingText` (if given) in place of the title, no clicks. */
  loading?: boolean;
  loadingText?: ReactNode;
  size?: keyof typeof PAD;
  /** What sits on the right: the chevron (the default) or something of your own; `false` for nothing. */
  trailing?: ReactNode | false;
  'aria-label'?: string;
  /** What it does, on hover (HintLayer), as on a Button. A disabled card shows `disabledReason` instead. */
  'data-hint'?: string;
  className?: string;
};

export type ChoiceCardProps = ChoiceCardBase &
  (
    | { onClick: () => void; href?: never }
    /** A page in the app to open: a real link, so Cmd-click and Open in new tab work. */
    | { href: string; onClick?: () => void }
  );

/**
 * One option that acts as soon as it's clicked: a team to switch to, a Xero organisation to link,
 * or (with `href`) a page to open. A card with a title, a line under it, something on the left and
 * a chevron. For one-of-a-few that is chosen and then saved, use `ChoiceCardGroup`.
 */
export function ChoiceCard({
  title,
  description,
  media,
  badge,
  onClick,
  href,
  disabled,
  disabledReason,
  loading = false,
  loadingText,
  size = 'default',
  trailing,
  className,
  ...aria
}: ChoiceCardProps) {
  const baseId = useId();
  const off = disabled || loading;
  const reason = disabled && !loading && disabledReason ? disabledReason : null;
  const classes = cn(
    CARD,
    PAD[size],
    'items-center focus-visible:outline-2 focus-visible:outline-ring',
    off ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent',
    loading && 'cursor-progress opacity-100',
    className
  );
  const left = loading ? <Spinner size="sm" /> : media;
  const content = (
    <>
      {left ? <span className="flex shrink-0 items-center">{left}</span> : null}
      <CardText title={loading && loadingText ? loadingText : title} description={description} badge={badge} />
      {trailing === false
        ? null
        : (trailing ?? <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />)}
    </>
  );
  // With a reason the wrapper below stands in for the card with screen readers, as Button does.
  const cardId = `${baseId}card`;
  const hidden = reason ? { id: cardId, 'aria-hidden': true as const } : null;
  const card =
    href && !off ? (
      <Link to={href} onClick={onClick} className={classes} {...aria}>
        {content}
      </Link>
    ) : (
      <button
        type="button"
        onClick={onClick}
        disabled={off}
        aria-busy={loading || undefined}
        className={classes}
        {...aria}
        {...hidden}
      >
        {content}
      </button>
    );
  if (!reason) return card;
  // A disabled button gets no pointer or focus events, so the reason sits on a wrapper that does.
  return (
    <span
      data-slot="choice-card-disabled-reason"
      data-hint={reason}
      role="button"
      aria-disabled="true"
      aria-labelledby={cardId}
      aria-describedby={`${baseId}reason`}
      tabIndex={0}
      className="block cursor-not-allowed rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      {card}
      <span id={`${baseId}reason`} hidden>
        {reason}
      </span>
    </span>
  );
}

export type ChoiceCardOption<T extends string> = {
  value: T;
  title: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
  media?: ReactNode;
  disabled?: boolean;
};

/**
 * One of a few options, as cards with a radio dot: the print format, how a store connects, what a
 * rule covers. Real radio buttons underneath, so arrow keys move the choice and a form posts it.
 * For two to four short words, use SegmentedControl; for a long list, a Select.
 */
export function ChoiceCardGroup<T extends string>({
  value,
  onValueChange,
  options,
  label,
  name,
  size = 'default',
  disabled,
  className,
}: {
  value: T;
  onValueChange: (value: T) => void;
  options: readonly ChoiceCardOption<T>[];
  /** The question, read out for the group ("How are you connecting?"); show it as a heading yourself if needed. */
  label: string;
  /** The radios' name; one is made up when not given. */
  name?: string;
  size?: keyof typeof PAD;
  disabled?: boolean;
  className?: string;
}) {
  const autoName = useId();
  const groupName = name ?? autoName;
  return (
    <div role="radiogroup" aria-label={label} className={cn('space-y-2', className)}>
      {options.map((option) => {
        const checked = option.value === value;
        const off = disabled || option.disabled;
        return (
          <label
            key={option.value}
            className={cn(
              CARD,
              PAD[size],
              'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring',
              off ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              checked ? 'border-primary bg-primary/5' : !off && 'hover:bg-muted/50'
            )}
          >
            <input
              type="radio"
              name={groupName}
              value={option.value}
              checked={checked}
              disabled={off}
              onChange={() => onValueChange(option.value)}
              className="sr-only"
            />
            <span
              aria-hidden
              className={cn(
                'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
                checked && 'border-primary'
              )}
            >
              {checked ? <span className="size-2 rounded-full bg-primary" /> : null}
            </span>
            {option.media ? <span className="flex shrink-0 items-center">{option.media}</span> : null}
            <CardText title={option.title} description={option.description} badge={option.badge} />
          </label>
        );
      })}
    </div>
  );
}
